import { Driver } from '../API/types/Drivers';
import { Vehicle, VehicleStatus } from '../API/types/Vehicles';
import { theme } from '../theme/theme';

export const generateEventsLabel = (events: number): string => `${events} alerts in total`;

export const generateScoreColor = (score: number): string => {
  if (score === 0) return theme.colors.outline;
  if (score >= 50) return theme.colors.main_cta;
  if (score < 50) return theme.colors.red;

  return theme.colors.inactive;
};

/**
 * Single source of truth for vehicle-status colouring: status labels and map markers
 * must agree, so both go through this. See DEFAULT_MARKER_COLOR in Map/markerColors.
 */
export const generateVehicleStatusColor = (status?: VehicleStatus | null): string => {
  switch (status) {
    case 'running':
      return theme.colors.green;
    case 'idling':
      return theme.colors.main_cta;
    case 'parked':
      return theme.colors.orange;
    case 'notResponding':
    default:
      return theme.colors.inactive;
  }
};

export const capitalizedWord = (word: string): string => {
  const firstLetter = word.charAt(0).toUpperCase();
  const remainingLetters = word.slice(1);
  return firstLetter + remainingLetters;
};

export const sanitizeFileName = (filename: string): string => filename.replace(/\./g, '_');

export const downloadFile = (data: any, filename: string, type: string): void => {
  const fileForDownloading = new Blob([data], { type });
  const a = document.createElement('a');
  const url = URL.createObjectURL(fileForDownloading);
  a.href = url;
  a.download = sanitizeFileName(filename);
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
};

type AnyObject = { [key: string]: any };

export function changeObjectKeysWithPrefix(obj: AnyObject | undefined, prefix: string): AnyObject {
  if (!obj) return {};
  return Object.keys(obj).reduce((acc, key) => {
    acc[`${prefix}${key}`] = obj[key];
    return acc;
  }, {} as AnyObject);
}

interface DataObject {
  [key: string]: string | number | boolean | undefined;
}

export const createCSV = (data: DataObject[]): string => {
  if (!data || data.length === 0) {
    console.error('No data provided');
    return '';
  }

  const csvRows: string[] = [];

  const headers = Object.keys(data[0]).filter((key) => {
    const value = data[0][key];
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  });
  csvRows.push(headers.join(','));

  data.forEach((row) => {
    const values = headers.map((header) => {
      const value = row[header];
      return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
    });
    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
};

export const getScoreLabel = (score: number): string => (score !== undefined ? `${Math.round(score)} score` : 'No score');

export const DEFAULT_NO_TRIP_SCORE = 100;
export const LEGACY_NO_TRIP_SCORE = 10;

export const isPlaceholderNoTripScore = (
  score: number | null | undefined,
  distanceInKm = 0,
): boolean => (
  !distanceInKm
  && (score === DEFAULT_NO_TRIP_SCORE || score === LEGACY_NO_TRIP_SCORE)
);

export const hasDisplayableEntityScore = (
  score: number | null | undefined,
  distanceInKm = 0,
): boolean => (
  score != null
  && score !== 0
  && !isPlaceholderNoTripScore(score, distanceInKm)
);

export const generateAlertCountLabel = (alerts: number | undefined, totalValue: boolean = false): string => (alert !== undefined ? `${alert} alerts${alert !== undefined && totalValue ? ' in total' : ''}` : 'N/A');

export const objectIsEmpty = (o: any): boolean => {
  if (typeof o === 'object') {
    return Object.keys(o).length === 0;
  }
  return false;
};
